import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import Controls from "../../atoms/Controls";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Event } from "./Calendar.type";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import WaitList from "../../components/ScheduleOverview/WaitList/WaitList";
import { AppointmentDto } from "../../api-client";
import { UserDto } from "../../api-client";
import DialogDefaultAction from "../../atoms/DialogDefaultAction";

export interface AddEventModalProps {
  open: boolean;
  event?: Event;
  continuous?: boolean;
  onClose?: () => void;
  onConfirm: (event: Event) => void;
  user?: UserDto;
  isTimesheetMode?: boolean;
}

export interface EventModal {
  title: string;
  loaType: string;
  start: Dayjs;
  end: Dayjs;
}

const absenceOptions = [
  { value: "vacation", label: "Urlaub" },
  { value: "sickness", label: "Krank" },
  { value: "sickness_sick_note", label: "Krank mit AU" },
  { value: "sickness_child", label: "Kindkrank" },
  { value: "sickness_child_sick_note", label: "Kindkrank mit AU" },
  { value: "training", label: "Fortbildung" },
  { value: "compensatory_leave", label: "Ü frei" },
  { value: "remaining_vacation", label: "Resturlaub" },
  { value: "special_leave", label: "Sonderurlaub" },
  { value: "parental_leave", label: "Elternzeit" },
  { value: "exempted", label: "Freigestellt" },
  { value: "professional_ban", label: "Berufsverbot" },
];

const initialFormValues = {
  title: "",
  loaType: "",
  start: dayjs(),
  end: dayjs(),
};

const AddEventModal = (props: AddEventModalProps) => {
  const { onClose, onConfirm, open, event, continuous, user, isTimesheetMode = false } = props;
  const [formState, setFormState] = useState<EventModal>(initialFormValues);
  const [eventType, setEventType] = useState(
    continuous ? "ContinuousEventTimeSlot" : "EventTimeSlot"
  );
  const [noneTherapistUser, setNoneTherapistUser] = useState(false);

  useEffect(() => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      start: dayjs(event?.start),
      end: dayjs(event?.end),
    }));
    
    const therpistRole = user?.roles?.find(r => r.id === "therapist");
    if (!therpistRole) {
      setNoneTherapistUser(true);
    }

    if (isTimesheetMode) {
      // In timesheet mode, default to WorkingHours
      setEventType("WorkingHours");
    } else {
      // In normal mode, use original logic - prioritize event.type if present
      if (event?.type) {
        setEventType(event.type);
      } else if (continuous) {
        setEventType("ContinuousEventTimeSlot");
      } else if (!therpistRole) {
        setEventType("SimpleWorkingHours");
      } else {
        setEventType("EventTimeSlot");
      }
    }

  }, [event, isTimesheetMode, continuous, user]);

  const handleClose = () => {
    setNoneTherapistUser(false);
    return onClose && onClose();
  };

  const handleAddEventClick = () => {    
    if (eventType === "LeaveOfAbsence" && formState.loaType === "") return;
    const newEvent: Event = {
      resourceId: event?.resourceId,
      title: formState.title,
      start: formState.start.toISOString(),
      end: formState.end.toISOString(),
      type: eventType,
      loaType: formState.loaType,
      continuous: continuous,
    };
    // console.log("event modal", newEvent);
    onConfirm && onConfirm(newEvent);
  };

  const handleInputChange = (fieldName: string, value: any) => {
    // console.log("Input change:", fieldName, value);
    setFormState((prevFormState) => ({
      ...prevFormState,
      [fieldName]: value,
    }));
  };

  const handleEventTypeChange = (event: React.MouseEvent<HTMLElement>, type: string) => {
    if (type !== null) setEventType(type);
  };

  return (
    <>
    <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
      <DialogTitle>Neuer Termin</DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <ToggleButtonGroup
              color="primary"
              value={eventType}
              exclusive
              onChange={handleEventTypeChange}
              aria-label="Platform"
            >
            {!noneTherapistUser ? (
            <Box>
              {isTimesheetMode ? (
                // Timesheet mode: only working hours are drawn here. Break
                // CREATION is deliberately not offered — the working-hours
                // blocks are draggable foreground events, so a selection can
                // only start outside them, where a break would violate the
                // §4 position rule anyway. Breaks are created in the main
                // calendar ("Pause") or via the Admin weekly plan; the
                // timesheet supports move/resize/split/delete.
                <ToggleButton value="WorkingHours">Arbeitszeit</ToggleButton>
              ) : (
                <>
                  <ToggleButton value={continuous ? "ContinuousEventTimeSlot" : "EventTimeSlot"}>Termin</ToggleButton>
                  {!continuous && <ToggleButton value="LeaveOfAbsence">Abwesenheit</ToggleButton>}
                  {!continuous && <ToggleButton value="LunchBreak">Pause</ToggleButton>}
                </>
              )}
            </Box>
            ) : (
              <ToggleButton value="SimpleWorkingHours">SimpleWorkingHours</ToggleButton>
            )}
            </ToggleButtonGroup>
          </Grid>

          {(eventType === "EventTimeSlot" || eventType === "ContinuousEventTimeSlot") && (
            <Grid item xs={12}>
              <Controls.Input
                label="Titel"
                style = {{width: "100%"}}
                onChange={(e: any) => handleInputChange("title", e.target.value)}
              />
            </Grid>

          )}
          {eventType === "LeaveOfAbsence" && (
              <Grid item xs={12}>
                <Controls.Select
                  style={{ width: "100%" }}
                  placeholder="Bitte auswählen"
                  label="Abwesenheitsart"
                  name="type"
                  value={formState.loaType}
                  showOptionLabel
                  options={absenceOptions}
                  onChange={(e: any) => handleInputChange("loaType", e.target.value)}
                  onBlur={(e: any) => handleInputChange("loaType", e.target.value)}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* Controlled by formState (not the drag event): binding to
                    event.end made the picker snap back after every change, so
                    multi-section edits (hour, then minutes) lost the earlier
                    section and the dialog never showed the chosen end time. */}
                <TimePicker
                  ampm={false}
                  label="ab"
                  value={formState.start}
                  onChange={(date: any) => handleInputChange("start", dayjs(date))}
                />
                <TimePicker
                  ampm={false}
                  label="bis"
                  value={formState.end}
                  onChange={(date: any) => handleInputChange("end", dayjs(date))}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="text">
            Abbrechen
          </Button>
          <Button onClick={handleAddEventClick} autoFocus>Bestätigen</Button>
        </DialogActions>
        <DialogDefaultAction onAction={handleAddEventClick} disabled={!open} />
      </Dialog>
    </>
  );
};

export default AddEventModal;
